import type { IntlShape } from 'react-intl-next';

import { matchesAnyKeyword } from './matchesAnyKeyword.ts';
import { cadenceKeywordMessages } from './statusUpdateSignalKeywordMessages.ts';

/** Cadences a status update can follow. */
export type StatusUpdateCadence = 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly';

/** Classification result; `undefined` when no cadence keyword matches. */
export type StatusUpdateCadenceResult = StatusUpdateCadence | undefined;

/**
 * Order in which cadences are tested. More specific cadences are checked
 * first so that overlapping text resolves deterministically.
 */
const CADENCE_PRECEDENCE: readonly StatusUpdateCadence[] = [
	'quarterly',
	'monthly',
	'biweekly',
	'weekly',
	'daily',
];

/**
 * Resolves the (localized) cadence keyword per cadence for the current locale.
 */
const getCadenceKeywords = (intl: IntlShape): Record<StatusUpdateCadence, string[]> => ({
	daily: [intl.formatMessage(cadenceKeywordMessages.daily)],
	weekly: [intl.formatMessage(cadenceKeywordMessages.weekly)],
	biweekly: [
		intl.formatMessage(cadenceKeywordMessages.biweekly),
		intl.formatMessage(cadenceKeywordMessages.biHypthenWeekly),
		intl.formatMessage(cadenceKeywordMessages.fortnightly),
	],
	monthly: [intl.formatMessage(cadenceKeywordMessages.monthly)],
	quarterly: [intl.formatMessage(cadenceKeywordMessages.quarterly)],
});

/**
 * Classifies the cadence of a status update from text.
 */
export const detectStatusUpdateCadence = (
	text: string,
	intl: IntlShape,
): StatusUpdateCadenceResult => {
	if (!text) {
		return undefined;
	}
	const cadenceKeywords = getCadenceKeywords(intl);
	for (const cadence of CADENCE_PRECEDENCE) {
		if (matchesAnyKeyword(text, cadenceKeywords[cadence])) {
			return cadence;
		}
	}
	return undefined;
};
